import { FC, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { selectors as i18nSelectors, useTranslations } from '@altermaind/core/i18n';
import { Box, Card, Typography, useThemeValue } from '@altermaind/web/ui';
import { Badge } from '@/_shared/components/Badge';
import { ClaimsComponent } from '@/_shared/components/Claims';
import { Tooltip } from '@/_shared/components/Tooltip';
import { Claims } from '@/_shared/constants/claims';
import { DEFAULT_LANGUAGE } from '@/_shared/constants/languages';
import { formatAllCurrencyAmount, getDurationFromDays } from '@/_shared/helpers';
import { useDialog } from '@/_shared/hooks';
import { useLocalizedFullDate } from '@/_shared/hooks/useLocalizedDateName';
import { useResponsiveStyle } from '@/_shared/hooks/useResponsiveStyle';
import { getSubscriptionCall } from '@/billing/state';
import type { DialogContactsPayload } from '@/core/components/DialogContacts/DialogContacts';
import { getOrganizationsCall } from '@/core/state/api/getOrganizationsCall';
import { getUserWarningsCall } from '@/core/state/api/getUserWarningsCall';
import { PlanType } from '@/swagger/constants/slash-enums';
import { MaxLearningLicenses } from './MaxLearningLicenses';
import { PlanDetailsCard } from './PlanDetailsCard';

export const PlanTab: FC = () => {
  const t = useTranslations('settings.tabs.plan');
  const { defaultOrganization, vatNumber } = getOrganizationsCall.hook();
  const { dateToLocalizedFullDate } = useLocalizedFullDate();
  const language = (useSelector(i18nSelectors.selectCurrentLanguage) as string) || DEFAULT_LANGUAGE;

  const {
    execute: executeGetSubscription,
    isActive,
    isTrialActive,
    trialDays,
    planType,
    learningLicenses,
    adminLicenses,
    pricePerUserAmount,
    pricePerUserCurrency,
    totalCurrency,
    paymentInterval,
    startAt,
    paymentMethod,
    paymentNextDate,
  } = getSubscriptionCall.hook();
  const { isSubscriptionMissing } = getUserWarningsCall.hook();

  const { genericResponsiveValue } = useResponsiveStyle();
  const gap = parseFloat(`${useThemeValue('sizes', 'xs')}`);
  const { openDialog: openContacts } = useDialog<DialogContactsPayload>('contacts');

  const perUserPriceLabel = formatAllCurrencyAmount(
    pricePerUserAmount,
    pricePerUserCurrency,
    language,
    { hideDecimals: Number.isInteger(pricePerUserAmount) },
  );
  const intervalCount = paymentInterval?.count ?? 0;
  const billingTypeLabel = paymentInterval?.type
    ? t(`billingInterval.${paymentInterval.type}.${intervalCount === 1 ? 'one' : 'other'}`, {
        count: intervalCount,
      })
    : '--';
  const paymentMethodLabel = paymentMethod ? t(`paymentMethods.${paymentMethod}`) : '--';
  const activationDateLabel =
    dateToLocalizedFullDate(startAt ? new Date(startAt) : undefined) ?? '--';
  const nextInvoiceLabel =
    dateToLocalizedFullDate(paymentNextDate ? new Date(paymentNextDate) : undefined) ?? '--';
  const trialSubtitleDuration = getDurationFromDays(trialDays);

  useEffect(() => {
    executeGetSubscription({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box>
      <Card mt='xs'>
        <Box
          display='flex'
          flexDirection={genericResponsiveValue({
            breakPoints: { desktop: 'row' },
            defaultValue: 'column',
          })}
          style={{ gap }}
        >
          <Box
            flex={1}
            display='flex'
            flexDirection={genericResponsiveValue({
              breakPoints: { mobile: 'column' },
              defaultValue: 'row',
            })}
            style={{ gap }}
          >
            <Box flex={1}>
              <Typography variant='p2' mb='xxs' color='grey/light'>
                {t('companyName')}
              </Typography>
              <Typography variant='p2/light'>{defaultOrganization || '--'}</Typography>
            </Box>
            <Box flex={1}>
              <Typography variant='p2' mb='xxs' color='grey/light'>
                {t('companyVatNumber')}
              </Typography>
              <Typography variant='p2/light'>{vatNumber || '--'}</Typography>
            </Box>
          </Box>
          <Box flex={1}>
            <ClaimsComponent claimKey={Claims.BILLING_WRITE}>
              <Typography variant='p2/light' color='grey/light'>
                {t('companyAlert')}
                <Typography
                  as='span'
                  variant='p2'
                  color='primary'
                  style={{ cursor: 'pointer' }}
                  onClick={() => openContacts({})}
                >
                  {` ${t('companyAlertLink')} `}
                </Typography>
                {t('companyAlertSuffix')}
              </Typography>
            </ClaimsComponent>
          </Box>
        </Box>
      </Card>

      {isSubscriptionMissing ? (
        <Card mt='xs'>
          <Box
            display='flex'
            flexDirection='column'
            alignItems='center'
            justifyContent='center'
            py='l'
          >
            <Typography variant='p1' mb='xs' color='primary/text'>
              {t('noActivePlan.title')}
            </Typography>
            <Typography
              variant='p2/light'
              color='grey/light'
              textAlign='center'
              style={{ whiteSpace: 'pre-line' }}
            >
              {t('noActivePlan.description')}
            </Typography>
          </Box>
        </Card>
      ) : (
        <>
          <Card mt='xs' bg='primary'>
            <Box display='flex' flexDirection='column'>
              <Box
                display='flex'
                flexDirection={genericResponsiveValue({
                  breakPoints: { mobile: 'column' },
                  defaultValue: 'row',
                })}
                style={{ gap }}
              >
                <Box flex={1} display='flex' flexDirection='column'>
                  <Box
                    display='flex'
                    flexDirection='row'
                    alignItems='center'
                    mb='xs'
                    style={{ gap }}
                  >
                    <Typography variant='h3'>
                      {planType
                        ? t(`planTitles.${planType}`, { defaultValue: t('yourPlan.title') })
                        : t('yourPlan.title')}
                    </Typography>
                    {(isActive || isTrialActive) && (
                      <Badge
                        label={
                          isTrialActive
                            ? t(`yourPlan.statusTrial.${trialDays === 1 ? 'one' : 'other'}`, {
                                count: trialDays,
                              })
                            : t('yourPlan.statusActive')
                        }
                        variant='outline'
                        textProps={{ variant: 'p3/light' }}
                      />
                    )}
                  </Box>
                  <Box display='flex' flexDirection='row' style={{ gap }}>
                    <Box flex={1}>
                      <Box display='flex' flexDirection='row' alignItems='center' mb='xxs'>
                        <Typography variant='p2'>{t('yourPlan.learningLicenses')}</Typography>
                        <Tooltip iconProps={{ size: 'xs' }} containerProps={{ ml: 'xxs' }}>
                          {t('yourPlan.learningLicensesTooltip')}
                        </Tooltip>
                      </Box>
                      <Typography variant='h3'>{learningLicenses}</Typography>
                    </Box>
                    <Box flex={1}>
                      <Box display='flex' flexDirection='row' alignItems='center' mb='xxs'>
                        <Typography variant='p2'>{t('yourPlan.adminLicenses')}</Typography>
                        <Tooltip iconProps={{ size: 'xs' }} containerProps={{ ml: 'xxs' }}>
                          {t('yourPlan.adminLicensesTooltip')}
                        </Tooltip>
                      </Box>
                      <Typography variant='h3'>{adminLicenses}</Typography>
                    </Box>
                  </Box>
                </Box>
                <Box
                  flex={1}
                  display='flex'
                  flexDirection='column'
                  alignItems={genericResponsiveValue({
                    breakPoints: { mobile: 'flex-start' },
                    defaultValue: 'flex-end',
                  })}
                  style={{
                    textAlign: genericResponsiveValue({
                      breakPoints: { mobile: 'left' },
                      defaultValue: 'right',
                    }),
                  }}
                >
                  {planType === PlanType.CUSTOM_OFFLINE ? (
                    <ClaimsComponent claimKey={Claims.BILLING_WRITE}>
                      <>
                        <Typography variant='p2/light'>
                          {t('yourPlan.offlineReviewOffer')}
                        </Typography>
                        <Typography
                          variant='p2'
                          style={{ textDecoration: 'underline', cursor: 'pointer' }}
                          onClick={() => openContacts({})}
                        >
                          {t('yourPlan.offlineReviewOfferCta')}
                        </Typography>
                      </>
                    </ClaimsComponent>
                  ) : (
                    <Box textAlign='left'>
                      {isTrialActive ? (
                        <Typography variant='special'>
                          {t('yourPlan.trialMonthlyPrice', {
                            price: formatAllCurrencyAmount(0, totalCurrency || 'EUR', language, {
                              hideDecimals: true,
                            }),
                          })}
                        </Typography>
                      ) : (
                        <Typography variant='special'>
                          {t('yourPlan.monthlyPricePerLicensePrice', { price: perUserPriceLabel })}
                          <Typography as='span' variant='p1'>
                            {t('yourPlan.monthlyPricePerLicenseSuffix')}
                          </Typography>
                        </Typography>
                      )}
                      {isTrialActive ? (
                        <Typography variant='p2/light' mt='xs'>
                          {t(
                            `yourPlan.trialPriceSubtitle.${trialSubtitleDuration.unit}.${trialSubtitleDuration.count === 1 ? 'one' : 'other'}`,
                            { price: perUserPriceLabel, count: trialSubtitleDuration.count },
                          )}
                        </Typography>
                      ) : (
                        <Typography variant='p2/light' fontSize='xs' mt='xs'>
                          {t('yourPlan.pricesVatExcluded')}
                        </Typography>
                      )}
                    </Box>
                  )}
                </Box>
              </Box>
              {planType === PlanType.CUSTOM_OFFLINE && (
                <Typography variant='p2/light' mt='l'>
                  {t('yourPlan.offlineContractDetails')}
                </Typography>
              )}
              <MaxLearningLicenses />
            </Box>
          </Card>
          {planType !== PlanType.CUSTOM_OFFLINE && (
            <PlanDetailsCard
              activationDateLabel={activationDateLabel}
              billingTypeLabel={billingTypeLabel}
              paymentMethodLabel={paymentMethodLabel}
              nextInvoiceLabel={nextInvoiceLabel}
              planType={planType}
            />
          )}
        </>
      )}
    </Box>
  );
};
